import { lastValueFrom } from 'rxjs';
import * as t from 'io-ts';

import type { HttpService } from 'portlet/common/services';

import { telemetryApiKey } from '../config/env';

export type EventID = number;

// codec
export const TelemetryConfigC = t.intersection([
    t.type({ PlatformID: t.string }),
    t.partial({
        ApplicationID: t.string,
        ComponentID: t.string,
        SubcomponentID: t.union([t.string, t.null]),
        EventTypeID: t.union([
            t.literal('portlet_data_loaded'),
            t.literal('portlet_data_load_failed'),
        ]),
    }),
]);

export type TelemetryConfigCodecType = t.TypeOf<typeof TelemetryConfigC>;

export type TelemetryConfig = {
    PlatformID: string;
    ApplicationID?: string;
    ComponentID?: string;
    SubcomponentID?: string | null;
    EventTypeID?: 'portlet_data_loaded' | 'portlet_data_load_failed';
};

export interface TelemetryRecord {
    EventID: EventID;
    Timestamp: string;
    UserID: string;
    PlatformID: string | number;
    ApplicationID: string | number;
    SessionID?: string;
    EventTypeID: string | number;
    ComponentID?: string;
    SubcomponentID?: string | null;
}

// NETWORK MODELS

export interface RequestModel<Payload = unknown> {
    payload?: Payload;
    id?: number | string;
}

export type TelemetryCreateRequest = Partial<
    Pick<
        TelemetryRecord,
        | 'UserID'
        | 'PlatformID'
        | 'ApplicationID'
        | 'EventTypeID'
        | 'SessionID'
        | 'ComponentID'
        | 'SubcomponentID'
    >
>;

export const telemetryDefaultConfig: TelemetryConfig = {
    PlatformID: 'vizit_prod',
    ApplicationID: 'portletCE',
};

export class TelemetryService {
    /* inject dependencies via constructor */
    constructor(
        public readonly http: HttpService,
        private readonly baseUrl: string,
    ) {}

    public async createRecord(payload: TelemetryCreateRequest = {}) {
        return this.postRecordRequest({ payload });
    }

    public async getRecords() {
        return this.getRecordsRequest();
    }

    private postRecordRequest({ payload }: RequestModel<TelemetryCreateRequest>) {
        const url = `${this.baseUrl}/records`;
        return lastValueFrom(
            this.http.post(url, {
                headers: {
                    'X-API-KEY': telemetryApiKey,
                },
                body: payload,
            }),
        );
    }

    private getRecordsRequest() {
        const url = `${this.baseUrl}/records`;
        return lastValueFrom(
            this.http.get(url, {
                headers: {
                    'X-API-KEY': telemetryApiKey,
                },
            }),
        );
    }
}
